import clsx from 'clsx';

interface CardProps {
  /** Card content */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Add hover effect */
  hoverable?: boolean;
  /** Padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const paddingClasses = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

/**
 * Card - Standard card container
 * 
 * @example
 * <Card>Content</Card>
 * <Card hoverable>Clickable card</Card>
 * <Card padding="lg">Large padding</Card>
 */
export function Card({
  children,
  className,
  hoverable = false,
  padding = 'md',
}: CardProps) {
  return (
    <div
      className={clsx(
        'bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg',
        paddingClasses[padding],
        hoverable && 'hover:shadow-sm dark:hover:shadow-neutral-900/50 hover:border-neutral-300 dark:hover:border-neutral-600 transition-all cursor-pointer',
        className
      )}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps {
  /** Header content */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * CardHeader - Card header with bottom border
 */
export function CardHeader({ children, className }: CardHeaderProps) {
  return (
    <div className={clsx('flex items-center justify-between pb-3 mb-3 border-b border-neutral-100 dark:border-neutral-700', className)}>
      {children}
    </div>
  );
}

interface CardTitleProps {
  /** Title text or content */
  children: React.ReactNode;
  /** Optional icon */
  icon?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
}

/**
 * CardTitle - Card title with optional icon
 */
export function CardTitle({ children, icon, className }: CardTitleProps) {
  return (
    <div className={clsx('flex items-center gap-2', className)}>
      {icon}
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{children}</h3>
    </div>
  );
}
